import { ROLELESS_ROLE_ID } from "Global/Base/Rights/constants";
import { IBaseItemRightsCollection, IItemRights, IItemRightsCollection, ItemRightsForRightLevels, RightType } from "../../../Global/Base/Rights/RightTypes";
import { invariant } from "../../../Global/Base/Utils/AssertUtils";
import { IWithOriginalRightsDefinition } from "../UI/Controls/ControlInterfaces";
import { MutableItemRightsCollection } from "./MutableItemRightsCollection";

export class ComponentRightsExtractor {
    /**
     * Vrací kolekci práv pro zadané control - profiltrovanou dle `types`.
     * @param control Control, kterého se to týká. Práva se načítají z {@link IWithOriginalRightsDefinition.originalRightsDefinition}.
     * @param types (Volitelný) Bitová maska s typy, které budou to výsledku zohledněny. Pokud maska není uvedena, vrátí se všechny.
     */
    public static loadRightsForTypes(control: IWithOriginalRightsDefinition, types?: RightType): IItemRightsCollection {
        const { originalRightsDefinition } = control;
        invariant(!!originalRightsDefinition, "[Rights] originalRightsDefinition must be defined");

        const rights = new MutableItemRightsCollection();
        const loadAllRightTypes = types == null;

        /**
         * Pro relevantní `rightType` (s ohledem na `types`) promítne odpovídající právo z `originalRightsDefinition` do `rights`.
         * @param rightType Právo k případnému zkopírování
         * @param rightField Field odpovídající právu `rightType`.
         * @param extraHandler Pokud zadáno a `rightType` je relevantní, pak se provede.
         */
        const handleRight = (rightType: RightType, rightField: keyof IBaseItemRightsCollection, extraHandler?: () => void) => {
            if (loadAllRightTypes || (types & rightType)) {
                const originalRight = originalRightsDefinition[rightField];

                // Z historických důvodů je žádoucí, aby - v případě undefined - výsledný objekt daný rightField vůbec neobsahoval. (Např. kvůli ControlInfoMessageBuilder.)
                if (originalRight !== undefined) {
                    rights[rightField] = originalRight;
                }
                extraHandler && extraHandler();
            }
        };

        handleRight(RightType.enabled, "visible", () => {
            rights.showIfDefault = originalRightsDefinition.showIfDefault;
        });
        handleRight(RightType.enabled, "enabled");
        handleRight(RightType.required, "required");
        handleRight(RightType.postponedRequired, "postponedRequired");
        handleRight(RightType.recommended, "recommended");
        handleRight(RightType.closeForm, "closeForm");
        handleRight(RightType.validateIf, "validateIf");
        handleRight(RightType.confirmIf, "confirmIf");
        handleRight(RightType.useDigitalSign, "useDigitalSign");
        handleRight(RightType.useEncryption, "useEncryption");
        handleRight(RightType.useDecryption, "useDecryption");
        handleRight(RightType.openAsEnabled, "openAsEnabled");

        return rights;
    }

    /**
     * Vrací ConditionID k jedné skupině práv (visible, enabled ..), které má smysl vyhodnocovat, protože mají stejné RoleID jako přihlášený uživatel nebo nemají RoleID
     */
    public static getValidConditionIDsForItemRights(rights: ReadonlyArray<IItemRights>, roleIDCollection: ReadonlyArray<string>): Array<string> {
        const IDs = new Array<string>();
        if (rights)
            for (let i = 0; i < rights.length; i++) {
                const aktRight = rights[i];
                if (aktRight.conditionID != null && (!aktRight.roleID || aktRight.roleID === ROLELESS_ROLE_ID || roleIDCollection.indexOf(aktRight.roleID) > -1)) {
                    IDs.push(aktRight.conditionID);
                }
            }
        return IDs;
    }

    public static getValidItemRightsForItemRights(itemRights: ReadonlyArray<IItemRights>, roleIDCollection: ReadonlyArray<string>): ItemRightsForRightLevels {
        const itemRightsForLevels = new ItemRightsForRightLevels();

        if (itemRights)
            for (let i = 0; i < itemRights.length; i++) {
                const aktRight = itemRights[i];
                if (!aktRight.roleID || aktRight.roleID === ROLELESS_ROLE_ID) {
                    itemRightsForLevels.addItemRights(aktRight);
                } else if (roleIDCollection.indexOf(aktRight.roleID) > -1) {
                    itemRightsForLevels.addItemRights(aktRight);
                }
            }

        return itemRightsForLevels;
    }

    public static getValidItemRightsForRightType(itemRights: IItemRightsCollection, rightType: RightType, roleIDCollection: ReadonlyArray<string>): ItemRightsForRightLevels | null {
        switch (rightType) {
            case RightType.visible:
                return this.getValidItemRightsForItemRights(itemRights.visible, roleIDCollection);
            case RightType.enabled:
                return this.getValidItemRightsForItemRights(itemRights.enabled, roleIDCollection);
            case RightType.required:
                return this.getValidItemRightsForItemRights(itemRights.required, roleIDCollection);
            case RightType.postponedRequired:
                return this.getValidItemRightsForItemRights(itemRights.postponedRequired, roleIDCollection);
            case RightType.recommended:
                return this.getValidItemRightsForItemRights(itemRights.recommended, roleIDCollection);
            case RightType.closeForm:
                return this.getValidItemRightsForItemRights(itemRights.closeForm, roleIDCollection);
            case RightType.validateIf:
                return this.getValidItemRightsForItemRights(itemRights.validateIf, roleIDCollection);
            case RightType.confirmIf:
                return this.getValidItemRightsForItemRights(itemRights.confirmIf, roleIDCollection);
            case RightType.useDigitalSign:
                return this.getValidItemRightsForItemRights(itemRights.useDigitalSign, roleIDCollection);
            case RightType.useEncryption:
                return this.getValidItemRightsForItemRights(itemRights.useEncryption, roleIDCollection);
            case RightType.useDecryption:
                return this.getValidItemRightsForItemRights(itemRights.useDecryption, roleIDCollection);
            case RightType.openAsEnabled:
                return this.getValidItemRightsForItemRights(itemRights.openAsEnabled, roleIDCollection);
            default:
                return null;
        }
    }

    public static getValidItemRightsForRightTypesMap(itemRights: IItemRightsCollection, rightTypes: RightType[], roleIDCollection: ReadonlyArray<string>): Map<RightType, ItemRightsForRightLevels> {
        return ComponentRightsExtractor._getValidItemRightsForRightTypes(itemRights, rightTypes, roleIDCollection) as Map<RightType, ItemRightsForRightLevels>;
    }

    public static getValidItemRightsForRightTypesArray(itemRights: IItemRightsCollection, rightTypes: RightType[], roleIDCollection: ReadonlyArray<string>): ReadonlyArray<IItemRights> {
        return ComponentRightsExtractor._getValidItemRightsForRightTypes(itemRights, rightTypes, roleIDCollection, true) as ReadonlyArray<IItemRights>;
    }

    private static _getValidItemRightsForRightTypes(itemRights: IItemRightsCollection, rightTypes: RightType[], roleIDCollection: ReadonlyArray<string>, returnFlat: boolean = false): Map<RightType, ItemRightsForRightLevels/*Array<ItemRights>*/> | ReadonlyArray<IItemRights> {
        let resultArray: IItemRights[] = [];
        const resultMap = new Map<RightType, ItemRightsForRightLevels>();

        // Vyfiltruje položky práv pro prázdnou roli + pro aktivní role
        for (const rightType of rightTypes) {
            const validItemRights = ComponentRightsExtractor.getValidItemRightsForRightType(itemRights, rightType, roleIDCollection);
            if (!validItemRights)
                continue;

            if (returnFlat)
                resultArray = resultArray.concat(validItemRights.flatten());
            else
                resultMap.set(rightType, validItemRights);
        }

        if (returnFlat)
            return resultArray;
        else
            return resultMap;
    }

    /**
     * Zjisti jestli skupina prav (visible, enabled ..), ma nejake pravo, ktere je platne pro prihlaseneho uzivatele (odpovida RoleID nebo nejake pravo nema RoleID a plati tedy pro vsechny role)
     */
    public static hasSomeValidRightToEvaluate(rights: ReadonlyArray<IItemRights>, roleIDCollection: ReadonlyArray<string>): boolean {
        if (rights) {
            for (let i = 0; i < rights.length; i++) {
                const aktRight = rights[i];
                if (!aktRight.roleID || aktRight.roleID === ROLELESS_ROLE_ID || roleIDCollection.indexOf(aktRight.roleID) > -1) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Vrátí seznam ConditionID od jednotlivých práv, která se budou vyhodnocovat (podle toho jestli mají RoleID jako přihlášený uživatel)
     */

    public static getAllRightsIDs(itemRights: IItemRightsCollection, roleIDCollection: ReadonlyArray<string>): Array<string> {
        let IDList = new Array<string>();

        //var IDList2 = new Array<number>();
        //var IDs = new Array<number>();
        //IDs.push(1);

        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.enabled, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.postponedRequired, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.recommended, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.required, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.visible, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.closeForm, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.validateIf, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.confirmIf, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.useDigitalSign, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.useEncryption, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.useDecryption, roleIDCollection));
        IDList = IDList.concat(ComponentRightsExtractor.getValidConditionIDsForItemRights(itemRights.openAsEnabled, roleIDCollection));
        return IDList;
    }
}
